import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle2 } from 'lucide-react';
import { Progress, addToast } from '@heroui/react';
import { uploadFileToS3 } from '@/utils/uploadFileToS3';
import { getAcceptStringForInput, AcceptedFileType, FILE_TYPE_CATEGORIES } from './utils';

export type UploadStage = 'idle' | 'getting-url' | 'uploading' | 'saving-metadata' | 'completed' | 'error';

export interface FileUploadProps {
  file: File | null;
  onChange: (file: File | null) => void;
  acceptedTypes?: AcceptedFileType[];
  maxSize?: number; // in MB
  isMediaLibrary?: boolean;
  onFileUploaded?: (fileMetadata: {
    id: string;
    name: string;
    url: string;
    size: number;
    type: string;
    provider: string;
    createdAt: string;
  }) => void;
  onError?: (error: string) => void;
  onProgressChange?: (progress: number) => void;
  onStageChange?: (stage: UploadStage) => void;
}

const getAllowedMimeTypes = (acceptedTypes: AcceptedFileType[]): string[] => {
  return acceptedTypes.flatMap((type) => {
    if (type in FILE_TYPE_CATEGORIES) {
      return [...FILE_TYPE_CATEGORIES[type as keyof typeof FILE_TYPE_CATEGORIES].mimeTypes];
    }
    return [];
  });
};

const FileUpload: React.FC<FileUploadProps> = ({
  file,
  onChange,
  acceptedTypes = [AcceptedFileType.PDF],
  maxSize = 10,
  isMediaLibrary = false,
  onFileUploaded,
  onError,
  onProgressChange,
  onStageChange,
}) => {
  const { t } = useTranslation('admin');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<UploadStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateProgress = (progress: number) => {
    setUploadProgress(progress);
    onProgressChange?.(progress);
  };

  const updateStage = (stage: UploadStage) => {
    setUploadStage(stage);
    onStageChange?.(stage);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      onChange(null);
      return;
    }

    setError(null);
    updateProgress(0);
    updateStage('getting-url');

    try {
      const allowedMimeTypes = getAllowedMimeTypes(acceptedTypes);
      const fileMetadata = await uploadFileToS3({
        file: selectedFile,
        filename: selectedFile.name,
        maxSizeMb: maxSize,
        isMediaLibrary,
        allowedMimeTypes: allowedMimeTypes.length > 0 ? allowedMimeTypes : undefined,
        onProgress: updateProgress,
        onStageChange: (stage) => updateStage(stage),
      });

      updateStage('completed');

      onChange(selectedFile);

      if (onFileUploaded) {
        onFileUploaded(fileMetadata);
      }

      addToast({
        title: 'Upload Successful',
        description: 'File uploaded successfully',
        color: 'success',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
      console.error('File upload error:', err);
      addToast({
        title: 'Upload Error',
        description: errorMessage,
        color: 'danger',
      });
      handleError(errorMessage);
      updateStage('error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onChange(null);
    }
  };

  const isUploading = ['getting-url', 'uploading', 'saving-metadata'].includes(uploadStage);
  const isCompleted = uploadStage === 'completed';
  const hasError = uploadStage === 'error';

  return (
    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptStringForInput(acceptedTypes)}
        onChange={handleFileChange}
        className="hidden"
        id="file-upload"
        disabled={isUploading}
      />
      <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center space-y-2">
        <Upload className="w-8 h-8 text-gray-400" />
        <div className="w-full">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {file ? file.name : t('guides.modal.form.file.placeholder', { type: acceptedTypes.join(', ') })}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('guides.modal.form.file.description', { type: acceptedTypes.join(', '), maxSize })}
          </p>
        </div>
      </label>

      {isUploading && (
        <div className="mt-4 space-y-2">
          <Progress
            aria-label="Upload progress"
            value={uploadProgress}
            className="w-full"
            color="primary"
            showValueLabel
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {uploadStage === 'getting-url' && 'Getting upload URL...'}
            {uploadStage === 'uploading' && 'Uploading to S3...'}
            {uploadStage === 'saving-metadata' && 'Saving file metadata...'}
          </p>
        </div>
      )}

      {isCompleted && file && (
        <div className="mt-4 flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">Upload complete</span>
        </div>
      )}

      {hasError && error && (
        <div className="mt-4 text-red-600 dark:text-red-400">
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
